import debounce from 'just-debounce';
import VanillaModal from 'vanilla-modal';
import {
  trapFocus,
  removeTrapFocus,
} from '@shopify/theme-a11y';
import EventHandler from '@pixelunion/events';

import ScrollLock from '../helpers/ScrollLock';

let openModals = [];

const unlockScrollLock = () => {
  if (openModals.length === 0) {
    ScrollLock.unlock();
  }
};

export default class Modal {
  constructor(options = {}) {
    this.body = document.body;

    this.modal = null;

    this.events = new EventHandler();

    // Extend default vanilla-modal callbacks back to instantiator of Modal
    this.defaultOptions = {
      onOpen: () => {},
      onClose: () => {},
      onBeforeOpen: () => {},
      onBeforeClose: () => {},
      modalId: null,
    };

    this.options = { ...this.defaultOptions, ...options };

    const modalSelector = this.options.modalId
      ? `[data-modal-container-${this.options.modalId}]`
      : '[data-modal-container]';

    const closeSelector = this.options.modalId
      ? `[data-modal-${this.options.modalId}-close`
      : '[data-modal-close]';

    this.loadedClass = this.options.modalId ? `modal-${this.options.modalId}-loaded` : 'modal-loaded';
    this.visibleClass = this.options.modalId ? `modal-${this.options.modalId}-visible` : 'modal-visible';

    this.modalEl = document.querySelector(modalSelector);
    this.modalInner = this.modalEl.querySelector('[data-modal-inner]');

    this._onOpen = this._onOpen.bind(this);
    this._onBeforeOpen = this._onBeforeOpen.bind(this);
    this._onClose = this._onClose.bind(this);
    this._onBeforeClose = this._onBeforeClose.bind(this);
    this._closeEsc = this._closeEsc.bind(this);

    this.position = this.position.bind(this);

    this.modalOptions = {
      modal: modalSelector,
      loadClass: '',
      class: this.loadedClass,
      close: closeSelector,
      onOpen: this._onOpen,
      onClose: this._onClose,
      onBeforeOpen: this._onBeforeOpen,
      onBeforeClose: this._onBeforeClose,
      transitions: false,
      closeKeys: [], // Override default "close on esc" so we have control
    };
  }

  unload() {
    if (!this.modal) return;

    this.modal.destroy();

    openModals = openModals.filter(modal => modal !== this);
    unlockScrollLock();
  }

  /**
   * Open a modal with contents from selector
   *
   * @param selector
   * @param handle
   */
  open(selector, handle = 'general') {
    this._addModalClass(handle);
    this.modal = new VanillaModal(this.modalOptions);
    this.modal.open(selector);
    openModals.push(this);
    this.events.register(window, 'keydown', this._closeEsc);
  }

  close() {
    this.modal.close();
    this.events.unregisterAll();
  }

  _closeEsc(e) {
    if (e.key === 'Escape' && openModals[openModals.length - 1] === this) {
      this.close();
    }
  }

  isOpen() {
    return this.modal && this.modal.isOpen;
  }

  /**
   * Update the vertical positioning of modal
   */
  position() {
    const windowHeight = window.innerHeight;

    const modalHeight = this.modalInner.getBoundingClientRect().height;
    const modalPadding = parseInt(window.getComputedStyle(this.modalEl).paddingTop, 10) * 2;

    const offset = (windowHeight - modalPadding - modalHeight) / 2;
    this.modalInner.style.marginTop = offset > 0 ? `${offset}px` : 0;
  }

  /**
   * Add a class to the modal for individual styling
   * @param handle
   * @private
   */
  _addModalClass(handle) {
    this.modalEl.classList.add(`modal--${handle}`);
  }

  /**
   * Remove modal class based on the handle
   * @private
   */
  _removeModalClass() {
    const modalClass = this.modalEl.className.match(/modal--[\w-]*\b/);
    if (!modalClass) {
      return;
    }

    this.modalEl.classList.remove(modalClass[0]);
  }

  _onClose() {
    this._removeModalClass();
    this.body.classList.remove(this.visibleClass);

    this.events.unregisterAll();

    this.modalInner.style.marginTop = '';

    this.options.onClose();

    removeTrapFocus(this.modalEl);

    if (this.activeElement) {
      // Ensure focus is properly re-trapped in the case when modal was
      // opened from another modal
      const focusTrap = this.activeElement.closest('[data-trap-focus]');
      if (focusTrap) {
        trapFocus(focusTrap);
      }

      this.activeElement.focus();
    }

    openModals = openModals.filter(modal => modal !== this);
    unlockScrollLock();
  }

  _onOpen() {
    this.activeElement = document.activeElement;
    this.position();
    ScrollLock.lock(this.modalEl);
    this.body.classList.add(this.visibleClass);
    this.events.register(window, 'resize', debounce(() => this.position(), 16, true, true));

    this.options.onOpen();

    trapFocus(this.modalEl);
  }

  _onBeforeClose() {
    this.options.onBeforeClose();
  }

  _onBeforeOpen() {
    this.options.onBeforeOpen();
  }
}
